import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ArrowLeft, Link2, Trash2 } from 'lucide-react'
import { CONTENT_SCHEMAS, CONTENT_TYPES, STATUSES, type ContentType } from '@mlopsmena/content'
import { api, ApiError, type Item } from '../lib/api'
import { useAuth } from '../lib/auth'
import { TYPES, type TypeConfig } from '../lib/types'
import { getPath, missingArabic, slugify, toFormValues } from '../lib/formValues'
import { EnglishOnlyContext, Fields, FormLangContext } from '../fields/Fields'
import { Notice, PageHeader, Spinner, StatusChip, ago } from '../components/ui'

export default function ContentEdit() {
  const { type, id } = useParams() as { type: ContentType; id?: string }
  const item = useQuery({
    queryKey: ['content', type, id],
    queryFn: () => api.get<{ item: Item }>(`/content/${type}/${id}`).then((r) => r.item),
    enabled: Boolean(id),
  })
  if (!CONTENT_TYPES.includes(type)) return <Notice>Unknown content type.</Notice>
  if (id && item.isPending) return <Spinner />
  if (id && item.isError) return <Notice>{(item.error as Error).message}</Notice>
  return <Editor key={`${type}:${id ?? 'new'}:${item.data?.updatedAt ?? ''}`} config={TYPES[type]} item={item.data} />
}

type Values = Record<string, unknown> & { slug: string; status: string }

function Editor({ config, item }: { config: TypeConfig; item?: Item }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuth()
  const [lang, setLang] = useState<'en' | 'ar'>('en')
  const [notice, setNotice] = useState<{ kind: 'error' | 'ok' | 'warn'; text: string; conflict?: boolean } | null>(null)

  const form = useForm<Values>({
    // The API validates with the same schema; this just catches mistakes before a round trip.
    resolver: zodResolver(CONTENT_SCHEMAS[config.type] as never),
    defaultValues: {
      slug: item?.slug ?? '',
      status: item?.status ?? 'draft',
      ...toFormValues(config.fields, item),
    } as Values,
    mode: 'onSubmit',
  })
  const { register, handleSubmit, formState, setValue, setError } = form

  // Suggest a slug from the title until someone types one.
  const slugTouched = useRef(Boolean(item))
  const slugSource = useWatch({ control: form.control, name: config.slugFrom }) as string | undefined
  useEffect(() => {
    if (!slugTouched.current && typeof slugSource === 'string') setValue('slug', slugify(slugSource))
  }, [slugSource, setValue])

  const slugLocked = Boolean(item && item.status !== 'draft' && user.role !== 'admin')
  const missing = missingArabic(useWatch({ control: form.control }), config.englishOnly)

  async function save(values: Values) {
    setNotice(null)
    try {
      if (item) {
        const res = await api.put<{ item: Item }>(`/content/${config.type}/${item.id}`, { ...values, updatedAt: item.updatedAt })
        qc.setQueryData(['content', config.type, item.id], res.item)
        await qc.invalidateQueries({ queryKey: ['content', config.type], exact: true })
        await qc.invalidateQueries({ queryKey: ['publish', 'status'] })
        setNotice({ kind: 'ok', text: res.item.status === 'published' ? 'Saved. It goes live on the next publish.' : 'Saved.' })
      } else {
        const res = await api.post<{ item: Item }>(`/content/${config.type}`, values)
        await qc.invalidateQueries({ queryKey: ['content', config.type], exact: true })
        await qc.invalidateQueries({ queryKey: ['publish', 'status'] })
        navigate(`/content/${config.type}/${res.item.id}`, { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiError) {
        for (const issue of err.issues) setError(issue.path as never, { message: issue.message })
        setNotice({ kind: 'error', text: err.message, conflict: err.code === 'conflict' })
      } else {
        setNotice({ kind: 'error', text: (err as Error).message })
      }
    }
  }

  async function remove() {
    if (!item || !window.confirm(`Delete "${config.title(item)}" for good? Archiving keeps it but hides it from the site.`)) return
    try {
      await api.del(`/content/${config.type}/${item.id}`)
      await qc.invalidateQueries({ queryKey: ['content', config.type] })
      navigate(`/content/${config.type}`)
    } catch (err) {
      setNotice({ kind: 'error', text: (err as Error).message })
    }
  }

  const errorCount = Object.keys(formState.errors).length

  return (
    <FormProvider {...form}>
      <FormLangContext.Provider value={lang}>
        <form onSubmit={handleSubmit(save, () => setNotice({ kind: 'error', text: 'Some fields need attention — they are marked in red.' }))}>
          <Link to={`/content/${config.type}`} className="mb-3 inline-flex items-center gap-1 text-sm text-muted hover:text-fg">
            <ArrowLeft className="h-3.5 w-3.5" /> {config.label}
          </Link>
          <PageHeader title={item ? config.title(item) || item.slug : `New ${config.singular}`}>
            {item && <StatusChip status={item.status} />}
          </PageHeader>

          {/* Sticky bar: language, status, save */}
          <div className="sticky top-[3.25rem] z-10 -mx-2 mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-line bg-surface/95 px-3 py-2 backdrop-blur">
            <div className="flex rounded-lg border border-line p-0.5" role="group" aria-label="Editing language">
              {(['en', 'ar'] as const).map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                        className={`rounded-md px-3 py-1 text-xs font-semibold ${lang === l ? 'bg-accent text-accent-fg' : 'text-muted'}`}>
                  {l === 'en' ? 'English' : 'العربية'}
                </button>
              ))}
            </div>
            {missing.length > 0 && (
              <button type="button" className="chip border-warn/40 text-warn" onClick={() => setLang('ar')} title={missing.join(', ')}>
                {missing.length} field{missing.length > 1 ? 's' : ''} without Arabic
              </button>
            )}
            <div className="ms-auto flex items-center gap-2">
              <select className="input !w-auto" {...register('status')} aria-label="Status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <button className="btn-primary" disabled={formState.isSubmitting}>
                {formState.isSubmitting && <Spinner />} {item ? 'Save' : 'Create'}
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {notice && (
              <Notice kind={notice.kind}>
                {notice.text}
                {notice.conflict && (
                  <> <button type="button" className="underline" onClick={() => qc.invalidateQueries({ queryKey: ['content', config.type, item?.id] })}>
                    Load their version
                  </button> (your unsaved edits will be lost).</>
                )}
              </Notice>
            )}
            {errorCount > 0 && !notice && <Notice>Some fields need attention.</Notice>}

            {!item && config.type === 'articles' && <Unfurl />}

            <div className="card space-y-5 p-5">
              <div>
                <label className="label" htmlFor="slug">Slug</label>
                <input
                  id="slug"
                  className="input font-mono"
                  readOnly={slugLocked}
                  {...register('slug', { onChange: () => { slugTouched.current = true } })}
                />
                <p className="mt-1 text-xs text-faint">
                  {slugLocked
                    ? 'Published, so the slug is locked — it is part of links people have shared. An admin can change it.'
                    : 'Lowercase words and hyphens. Used in URLs and as the stable id.'}
                </p>
                {(getPath(formState.errors, 'slug') as { message?: string } | undefined)?.message && (
                  <p className="mt-1 text-xs text-danger">{(getPath(formState.errors, 'slug') as { message: string }).message}</p>
                )}
              </div>
              <EnglishOnlyContext.Provider value={config.englishOnly ?? []}>
                <Fields fields={config.fields} />
              </EnglishOnlyContext.Provider>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-faint">
              <span>{item && <>Last saved {ago(item.updatedAt)}. Saving with “published” makes it part of the next publish.</>}</span>
              {item && user.role === 'admin' && (
                <button type="button" className="btn-danger" onClick={() => void remove()}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete for good
                </button>
              )}
            </div>
          </div>
        </form>
      </FormLangContext.Provider>
    </FormProvider>
  )
}

/** New article: fill the form from a LinkedIn or Medium link. */
function Unfurl() {
  const { setValue, getValues } = useFormContext<Record<string, unknown>>()
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)

  async function go() {
    setBusy(true)
    setMsg(null)
    try {
      const { article } = await api.post<{ article: { slug: string; href: string; platform: string; title: string | null; description: string | null; date: string | null } }>(
        '/tools/unfurl', { url },
      )
      setValue('href', article.href)
      setValue('platform', article.platform)
      if (article.title) setValue('title', { ...(getValues('title') as object), en: article.title })
      if (article.description) setValue('description', { ...(getValues('description') as object), en: article.description })
      if (article.date) setValue('date', article.date)
      if (article.slug) setValue('slug', article.slug)
      setMsg(article.title ? 'Filled in from the page — check the text and add the Arabic.' : 'The page did not share its details; fill them in by hand.')
    } catch (err) {
      setMsg((err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card flex flex-wrap items-end gap-2 p-4">
      <div className="min-w-64 flex-1">
        <label className="label" htmlFor="unfurl">Start from a link</label>
        <input id="unfurl" className="input" placeholder="https://www.linkedin.com/pulse/…" value={url} onChange={(e) => setUrl(e.target.value)} />
      </div>
      <button type="button" className="btn" disabled={!url || busy} onClick={() => void go()}>
        {busy ? <Spinner /> : <Link2 className="h-3.5 w-3.5" />} Fetch details
      </button>
      {msg && <p className="w-full text-xs text-muted">{msg}</p>}
    </div>
  )
}
